import { useQueryClient } from '@tanstack/react-query';

import { useFishGroupsController_create } from '@/services/hooks';
import type { CreateFishGroupRequest } from '@/services/types';

import type { MutationState } from '../shared/mutation';

import { invalidateFishGroupQueries } from './_invalidateQueries';

export interface UseCreateFishGroupResult extends MutationState {
    create: (requestBody: CreateFishGroupRequest) => Promise<void>;
}

interface UseCreateFishGroupOptions {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

export const useCreateFishGroup = (
    clientId: number,
    options?: UseCreateFishGroupOptions
): UseCreateFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useFishGroupsController_create();

    const create = async (requestBody: CreateFishGroupRequest): Promise<void> => {
        try {
            await mutation.mutateAsync({ clientId, requestBody });
            await invalidateFishGroupQueries(queryClient, clientId);
            options?.onSuccess?.();
        } catch (error) {
            options?.onError?.(error);
            throw error;
        }
    };

    return {
        create,
        isLoading: mutation.isPending,
        error: mutation.error,
        reset: mutation.reset,
    };
};
